/**
 * AdsCampaignsListPage — Tela de lista de campanhas Meta Ads (/ads/campaigns).
 *
 * Fonte da verdade do fluxo/estados:
 *   docs/execucao-projetos/_local/design/wireframes/2026-06-09-analise-campanha-isolada.md
 *
 * Contrato de API consumido:
 *   GET /ads/campaigns?period=30d  → CampaignListResponse
 *
 * Estados honestos implementados:
 *   loading      → SkeletonTable (proporcoes reais)
 *   empty-no-source  → Meta Ads nao conectado (CTA nominal)
 *   empty-no-campaigns → nenhuma campanha no periodo
 *   ready        → tabela ordenavel com ref chips
 *   error        → mensagem + retry + suporte (sem stack trace)
 *
 * Invariantes:
 *   - Zero string hardcoded fora de i18n.ts (ads.*)
 *   - Zero acesso direto a env vars
 *   - ROAS null → "—" + explicacao (R-063: sem conversao configurada)
 *   - Dado null → "—" nunca "0"
 *   - Selecao maximo 3 campanhas para comparacao (D-CAMP-04)
 *   - Ordenacao e local (sem nova requisicao)
 *   - A11y AA: contrast, focus, aria, keyboard nav, role=columnheader
 *
 * F2 — 2026-06-09
 */

import '../styles/screen-ads.css';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { apiClient, ApiError } from '../lib/api-client';
import { Skeleton } from '../components/Skeleton';

// ---------------------------------------------------------------------------
// Tipos importados do contrato de API
// ---------------------------------------------------------------------------

interface CampaignKpis {
  readonly spend: number | null;
  readonly clicks: number | null;
  readonly cpc: number | null;
  readonly ctr: number | null;
  readonly impressions: number | null;
  readonly cpm: number | null;
  readonly roas: null; // R-063: sempre null em F2
  readonly frequency: number | null;
}

interface CampaignItem {
  readonly id: string;
  readonly name: string;
  readonly status: string | null;
  readonly kpis: CampaignKpis;
  readonly ref_id: string;
  readonly data_quality: 'full' | 'partial' | 'none';
}

interface CampaignListResponse {
  readonly campaigns: readonly CampaignItem[];
  readonly no_campaigns: boolean;
  readonly source: {
    readonly name: 'Meta Ads';
    readonly period_from: string;
    readonly period_to: string;
  };
  readonly summary: {
    readonly spend: number | null;
    readonly clicks: number | null;
    readonly cpc: number | null;
    readonly impressions: number | null;
  };
}

// ---------------------------------------------------------------------------
// Load state
// ---------------------------------------------------------------------------

type LoadState =
  | { kind: 'loading' }
  | { kind: 'empty-no-source' }
  | { kind: 'empty-no-campaigns' }
  | { kind: 'ready'; data: CampaignListResponse }
  | { kind: 'error' };

// ---------------------------------------------------------------------------
// Ordenacao
// ---------------------------------------------------------------------------

type SortField = 'spend' | 'clicks' | 'cpc' | 'ctr' | 'name';
type SortDir = 'asc' | 'desc';

// ---------------------------------------------------------------------------
// Helpers de formatacao (sem inventar dado — null → "—")
// ---------------------------------------------------------------------------

function fmtCurrency(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtNumber(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('pt-BR');
}

function fmtPercent(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return `${v.toFixed(2)}%`;
}

function statusLabel(status: string | null): string {
  if (status === null) return t('ads.statusUnknown');
  const s = status.toUpperCase();
  if (s === 'ACTIVE') return t('ads.statusActive');
  if (s === 'PAUSED') return t('ads.statusPaused');
  if (s === 'ARCHIVED') return t('ads.statusArchived');
  if (s === 'IN_REVIEW') return t('ads.statusInReview');
  return status;
}

function statusCssClass(status: string | null): string {
  if (status === null) return 'archived';
  const s = status.toUpperCase();
  if (s === 'ACTIVE') return 'active';
  if (s === 'PAUSED') return 'paused';
  if (s === 'ARCHIVED') return 'archived';
  if (s === 'IN_REVIEW') return 'in-review';
  return 'archived';
}

function periodFromMode(mode: string): string {
  if (mode === '7d') return t('ads.period7d');
  if (mode === '90d') return t('ads.period90d');
  if (mode === '365d') return t('ads.period365d');
  return t('ads.period30d');
}

function buildApiUrl(period: string): string {
  return `/ads/campaigns?period=${period}`;
}

// ---------------------------------------------------------------------------
// Painel de rastreabilidade (ref chip)
// ---------------------------------------------------------------------------

interface RefPanelData {
  readonly metric: string;
  readonly campaignName: string;
  readonly refId: string;
  readonly periodFrom: string;
  readonly periodTo: string;
}

function AdsRefPanel({
  data,
  onClose,
}: {
  readonly data: RefPanelData;
  readonly onClose: () => void;
}): JSX.Element {
  return (
    <div className="ads-ref-panel" role="dialog" aria-modal="true" aria-labelledby="ads-ref-title">
      <h3 className="ads-ref-panel-title" id="ads-ref-title">
        {t('ads.refPanelTitle')}
      </h3>
      <div className="ads-ref-panel-row">
        <span className="ads-ref-panel-row-label">{t('ads.refPanelMetric')}</span>
        <span className="ads-ref-panel-row-value">{data.metric}</span>
      </div>
      <div className="ads-ref-panel-row">
        <span className="ads-ref-panel-row-label">{t('ads.refPanelSource')}</span>
        <span className="ads-ref-panel-row-value">Meta Ads Graph API</span>
      </div>
      <div className="ads-ref-panel-row">
        <span className="ads-ref-panel-row-label">{t('ads.refPanelCampaign')}</span>
        <span className="ads-ref-panel-row-value">
          {data.campaignName} (ID: {data.refId.split('#')[2] ?? data.refId})
        </span>
      </div>
      <div className="ads-ref-panel-row">
        <span className="ads-ref-panel-row-label">{t('ads.refPanelPeriod')}</span>
        <span className="ads-ref-panel-row-value">
          {data.periodFrom} — {data.periodTo}
        </span>
      </div>
      <button type="button" className="ads-ref-panel-close" onClick={onClose}>
        {t('ads.refPanelClose')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton da tabela
// ---------------------------------------------------------------------------

function AdsTableSkeleton(): JSX.Element {
  return (
    <div
      className="ads-table-skeleton"
      role="status"
      aria-label="Carregando campanhas"
      aria-busy="true"
    >
      <div className="ads-table-skeleton-header" aria-hidden="true">
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="ads-table-skeleton-row" aria-hidden="true">
          <Skeleton variant="text" width="85%" />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="80%" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty states
// ---------------------------------------------------------------------------

function EmptyNoSource(): JSX.Element {
  return (
    <div className="ads-empty-state" aria-live="polite">
      <span className="ads-empty-state-icon material-symbols-rounded" aria-hidden="true">
        campaign
      </span>
      <h2 className="ads-empty-state-title">{t('ads.emptyNoSource')}</h2>
      <p className="ads-empty-state-desc">{t('ads.emptyNoSourceDesc')}</p>
      <p className="ads-empty-state-hint">{t('ads.emptyNoSourceHint')}</p>
      <div className="ads-empty-state-actions">
        <Link to="/sources?focus=meta_ads" className="ads-btn-primary">
          {t('ads.emptyNoSourceCta')}
        </Link>
        <Link to="/dashboard" className="ads-btn-secondary">
          {t('ads.backToDashboard')}
        </Link>
      </div>
    </div>
  );
}

function EmptyNoCampaigns({
  onExpandPeriod,
}: {
  readonly onExpandPeriod: () => void;
}): JSX.Element {
  return (
    <div className="ads-empty-state" aria-live="polite">
      <span className="ads-empty-state-icon material-symbols-rounded" aria-hidden="true">
        search_off
      </span>
      <h2 className="ads-empty-state-title">{t('ads.emptyNoCampaigns')}</h2>
      <p className="ads-empty-state-desc">{t('ads.emptyNoCampaignsDesc')}</p>
      <div className="ads-empty-state-actions">
        <button type="button" className="ads-btn-primary" onClick={onExpandPeriod}>
          {t('ads.emptyNoCampaignsCta')}
        </button>
        <Link to="/sources" className="ads-btn-secondary">
          {t('ads.emptyNoCampaignsVerify')}
        </Link>
        <Link to="/dashboard" className="ads-btn-secondary">
          {t('ads.emptyNoCampaignsBack')}
        </Link>
      </div>
    </div>
  );
}

function ErrorState({ onRetry }: { readonly onRetry: () => void }): JSX.Element {
  return (
    <div className="ads-empty-state" role="alert" aria-live="assertive">
      <span className="ads-empty-state-icon material-symbols-rounded" aria-hidden="true">
        wifi_off
      </span>
      <h2 className="ads-empty-state-title">{t('ads.errorTitle')}</h2>
      <p className="ads-empty-state-desc">{t('ads.errorDesc')}</p>
      <div className="ads-empty-state-actions">
        <button type="button" className="ads-btn-primary" onClick={onRetry}>
          {t('ads.errorRetry')}
        </button>
        <Link to="/sources" className="ads-btn-secondary">
          {t('ads.errorSupport')}
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Linha da tabela de campanha
// ---------------------------------------------------------------------------

interface CampaignRowProps {
  readonly campaign: CampaignItem;
  readonly periodFrom: string;
  readonly periodTo: string;
  readonly isSelected: boolean;
  readonly canSelect: boolean;
  readonly onSelect: (id: string) => void;
  readonly onRefClick: (data: RefPanelData) => void;
}

function CampaignRow({
  campaign,
  periodFrom,
  periodTo,
  isSelected,
  canSelect,
  onSelect,
  onRefClick,
}: CampaignRowProps): JSX.Element {
  const { kpis } = campaign;

  const handleRef = useCallback(
    (metric: string) => {
      onRefClick({
        metric,
        campaignName: campaign.name,
        refId: campaign.ref_id,
        periodFrom,
        periodTo,
      });
    },
    [campaign.name, campaign.ref_id, periodFrom, periodTo, onRefClick],
  );

  return (
    <tr className={isSelected ? 'ads-row-selected' : ''}>
      <td>
        <Link
          to={`/digital/campanhas/${campaign.id}`}
          style={{ fontWeight: 600, color: 'var(--md-on-surface)', textDecoration: 'none' }}
        >
          {campaign.name}
        </Link>
      </td>
      <td>
        <span className={`ads-status-badge ${statusCssClass(campaign.status)}`}>
          {statusLabel(campaign.status)}
        </span>
      </td>
      <td>
        {kpis.spend !== null ? (
          <span>
            {fmtCurrency(kpis.spend)}{' '}
            <button
              type="button"
              className="ads-ref-chip"
              onClick={() => handleRef(t('ads.kpiSpend'))}
              aria-label={`${t('ads.refChipLabel')} — ${t('ads.kpiSpend')}`}
            >
              [ref:]
            </button>
          </span>
        ) : (
          <span>—</span>
        )}
      </td>
      <td>
        {kpis.clicks !== null ? (
          <span>
            {fmtNumber(kpis.clicks)}{' '}
            <button
              type="button"
              className="ads-ref-chip"
              onClick={() => handleRef(t('ads.kpiClicks'))}
              aria-label={`${t('ads.refChipLabel')} — ${t('ads.kpiClicks')}`}
            >
              [ref:]
            </button>
          </span>
        ) : (
          <span>—</span>
        )}
      </td>
      <td>
        {kpis.cpc !== null ? (
          <span>
            {fmtCurrency(kpis.cpc)}{' '}
            <button
              type="button"
              className="ads-ref-chip"
              onClick={() => handleRef(t('ads.kpiCpc'))}
              aria-label={`${t('ads.refChipLabel')} — ${t('ads.kpiCpc')}`}
            >
              [ref:]
            </button>
          </span>
        ) : (
          <span>—</span>
        )}
      </td>
      <td>
        <div className="ads-table-actions">
          <Link
            to={`/digital/campanhas/${campaign.id}`}
            className="ads-btn-view"
            aria-label={`${t('ads.viewCampaign')}: ${campaign.name}`}
          >
            {t('ads.viewCampaign')}
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              arrow_forward
            </span>
          </Link>
          <button
            type="button"
            className={`ads-btn-select${isSelected ? ' selected' : ''}`}
            onClick={() => onSelect(campaign.id)}
            disabled={!canSelect && !isSelected}
            aria-pressed={isSelected}
            aria-label={
              isSelected
                ? `${t('ads.deselectCompare')}: ${campaign.name}`
                : `${t('ads.selectCompare')}: ${campaign.name}`
            }
            title={!canSelect && !isSelected ? t('ads.compareMaxTooltip') : undefined}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              {isSelected ? 'check_box' : 'check_box_outline_blank'}
            </span>
            {isSelected ? t('ads.deselectCompare') : t('ads.selectCompare')}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Barra de comparacao
// ---------------------------------------------------------------------------

interface CompareBarProps {
  readonly selectedIds: readonly string[];
  readonly campaigns: readonly CampaignItem[];
  readonly onCompare: () => void;
  readonly onClear: () => void;
}

function CompareBar({ selectedIds, campaigns, onCompare, onClear }: CompareBarProps): JSX.Element {
  const names = selectedIds.map((id) => campaigns.find((c) => c.id === id)?.name ?? id).join(', ');

  const label =
    selectedIds.length === 1
      ? t('ads.compareBarSingle')
      : t('ads.compareBarPlural', { count: String(selectedIds.length) });

  return (
    <div className="ads-compare-bar" role="region" aria-label="Comparacao de campanhas">
      <div>
        <div className="ads-compare-bar-info">
          {label}: <strong>{names}</strong>
        </div>
      </div>
      <div className="ads-compare-bar-actions">
        <button type="button" className="ads-btn-compare" onClick={onCompare}>
          <span className="material-symbols-rounded" style={{ fontSize: 16 }} aria-hidden="true">
            compare_arrows
          </span>
          {t('ads.compareNow')}
        </button>
        <button type="button" className="ads-btn-clear" onClick={onClear}>
          {t('ads.clearSelection')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

const MAX_COMPARE = 3;

const SORT_FIELDS: readonly {
  field: SortField;
  labelKey:
    | 'ads.filterSortSpend'
    | 'ads.filterSortClicks'
    | 'ads.filterSortCpc'
    | 'ads.filterSortCtr'
    | 'ads.filterSortName';
}[] = [
  { field: 'spend', labelKey: 'ads.filterSortSpend' },
  { field: 'clicks', labelKey: 'ads.filterSortClicks' },
  { field: 'cpc', labelKey: 'ads.filterSortCpc' },
  { field: 'ctr', labelKey: 'ads.filterSortCtr' },
  { field: 'name', labelKey: 'ads.filterSortName' },
];

export function AdsCampaignsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Periodo (default: 30d)
  const [period, setPeriod] = useState<string>(searchParams.get('period') ?? '30d');

  // Sort
  const [sortField, setSortField] = useState<SortField>('spend');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Selecao para comparacao
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);

  // Ref panel
  const [refPanelData, setRefPanelData] = useState<RefPanelData | null>(null);

  // Retry key
  const [retryKey, setRetryKey] = useState<number>(0);

  // Estado de carga
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  // Carregar campanhas
  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });

    const load = async (): Promise<void> => {
      try {
        const data = await apiClient.get<CampaignListResponse>(buildApiUrl(period));
        if (cancelled) return;

        // Cold start / sem fonte conectada: API retorna campaigns=[] e no_campaigns=true
        // com summary.spend=null
        if (data.campaigns.length === 0 && data.summary.spend === null) {
          setState({ kind: 'empty-no-source' });
          return;
        }

        if (data.no_campaigns || data.campaigns.length === 0) {
          setState({ kind: 'empty-no-campaigns' });
          return;
        }

        setState({ kind: 'ready', data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: 'empty-no-source' });
          return;
        }
        setState({ kind: 'error' });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [period, retryKey]);

  // Ordenacao local (sem nova request)
  const sortedCampaigns = useMemo(() => {
    if (state.kind !== 'ready') return [];

    const list = [...state.data.campaigns];
    list.sort((a, b) => {
      let va: number | string | null = null;
      let vb: number | string | null = null;

      if (sortField === 'name') {
        va = a.name.toLowerCase();
        vb = b.name.toLowerCase();
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      }

      va = a.kpis[sortField];
      vb = b.kpis[sortField];

      // null sempre vai para o fim
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      return sortDir === 'asc' ? (va as number) - (vb as number) : (vb as number) - (va as number);
    });

    return list;
  }, [state, sortField, sortDir]);

  const handleSortClick = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortField(field);
        setSortDir('desc');
      }
    },
    [sortField],
  );

  const handleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, id];
    });
  }, []);

  const handleCompare = useCallback(() => {
    if (selectedIds.length >= 2) {
      void navigate(`/digital/campanhas/compare?ids=${selectedIds.join(',')}&period=${period}`);
    }
  }, [navigate, selectedIds, period]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleExpandPeriod = useCallback(() => {
    // Avanca para o proximo periodo maior
    if (period === '7d') setPeriod('30d');
    else if (period === '30d') setPeriod('90d');
    else if (period === '90d') setPeriod('365d');
    else setPeriod('365d');
  }, [period]);

  const sortArrow = (field: SortField): string => {
    if (field !== sortField) return '';
    return sortDir === 'desc' ? ' ▼' : ' ▲';
  };

  const periodLabel = periodFromMode(period);

  // Source info do estado ready
  const sourceInfo = state.kind === 'ready' ? state.data.source : null;
  const summary = state.kind === 'ready' ? state.data.summary : null;
  const campaigns = state.kind === 'ready' ? state.data.campaigns : [];

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="ads-breadcrumb" aria-label="Navegacao">
        <Link to="/dashboard">
          <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
            arrow_back
          </span>
          {t('ads.backToTraffic')}
        </Link>
      </nav>

      {/* Page header */}
      <div className="ads-page-header">
        <div className="ads-page-eyebrow">Meta Ads</div>
        <h1 className="ads-page-title">
          {t('ads.listTitle')} · {periodLabel}
        </h1>
        <p className="ads-page-subtitle">{t('ads.listSubtitle')}</p>

        {sourceInfo !== null ? (
          <div className="ads-page-meta">
            <span className="ads-page-meta-item">
              <span
                className="material-symbols-rounded"
                style={{ fontSize: 14 }}
                aria-hidden="true"
              >
                calendar_month
              </span>
              {sourceInfo.period_from} — {sourceInfo.period_to}
            </span>
          </div>
        ) : null}
      </div>

      {/* Filtros */}
      <div className="ads-filters" role="group" aria-label={t('ads.filterPeriod')}>
        <div className="ads-filter-group">
          <label htmlFor="ads-period-select" className="ads-filter-label">
            {t('ads.filterPeriod')}:
          </label>
          <select
            id="ads-period-select"
            className="ads-filter-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            aria-label={t('ads.filterPeriod')}
          >
            <option value="7d">{t('ads.period7d')}</option>
            <option value="30d">{t('ads.period30d')}</option>
            <option value="90d">{t('ads.period90d')}</option>
            <option value="365d">{t('ads.period365d')}</option>
          </select>
        </div>

        <div className="ads-filter-group">
          <label htmlFor="ads-sort-select" className="ads-filter-label">
            {t('ads.filterSort')}:
          </label>
          <select
            id="ads-sort-select"
            className="ads-filter-select"
            value={sortField}
            onChange={(e) => {
              setSortField(e.target.value as SortField);
              setSortDir('desc');
            }}
            aria-label={t('ads.filterSort')}
          >
            {SORT_FIELDS.map((sf) => (
              <option key={sf.field} value={sf.field}>
                {t(sf.labelKey)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Resumo da conta */}
      {summary !== null && state.kind === 'ready' ? (
        <div className="ads-account-summary" aria-label={t('ads.summaryTitle')}>
          <div className="ads-summary-item">
            <span className="ads-summary-label">{t('ads.summarySpend')}</span>
            <span className="ads-summary-value">{fmtCurrency(summary.spend)}</span>
            <span className="ads-summary-via">{t('ads.summaryVia')}</span>
          </div>
          <div className="ads-summary-item">
            <span className="ads-summary-label">{t('ads.summaryClicks')}</span>
            <span className="ads-summary-value">{fmtNumber(summary.clicks)}</span>
            <span className="ads-summary-via">{t('ads.summaryVia')}</span>
          </div>
          {summary.cpc !== null ? (
            <div className="ads-summary-item">
              <span className="ads-summary-label">{t('ads.summaryCpc')}</span>
              <span className="ads-summary-value">{fmtCurrency(summary.cpc)}</span>
              <span className="ads-summary-via">{t('ads.kpiCalculated')}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Loading */}
      {state.kind === 'loading' ? <AdsTableSkeleton /> : null}

      {/* Empty: sem fonte */}
      {state.kind === 'empty-no-source' ? <EmptyNoSource /> : null}

      {/* Empty: sem campanhas no periodo */}
      {state.kind === 'empty-no-campaigns' ? (
        <EmptyNoCampaigns onExpandPeriod={handleExpandPeriod} />
      ) : null}

      {/* Erro */}
      {state.kind === 'error' ? (
        <ErrorState
          onRetry={() => {
            setRetryKey((k) => k + 1);
          }}
        />
      ) : null}

      {/* Tabela de campanhas */}
      {state.kind === 'ready' && sortedCampaigns.length > 0 ? (
        <div className="ads-table-wrapper">
          <table className="ads-table" aria-label={t('ads.listTitle')}>
            <thead>
              <tr>
                <th scope="col">
                  <button type="button" onClick={() => handleSortClick('name')}>
                    {t('ads.colName')}
                    {sortArrow('name')}
                  </button>
                </th>
                <th scope="col">{t('ads.colStatus')}</th>
                <th scope="col">
                  <button type="button" onClick={() => handleSortClick('spend')}>
                    {t('ads.colSpend')}
                    {sortArrow('spend')}
                  </button>
                </th>
                <th scope="col">
                  <button type="button" onClick={() => handleSortClick('clicks')}>
                    {t('ads.colClicks')}
                    {sortArrow('clicks')}
                  </button>
                </th>
                <th scope="col">
                  <button type="button" onClick={() => handleSortClick('cpc')}>
                    {t('ads.colCpc')}
                    {sortArrow('cpc')}
                  </button>
                </th>
                <th scope="col">{/* Acoes */}</th>
              </tr>
            </thead>
            <tbody>
              {sortedCampaigns.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  periodFrom={sourceInfo?.period_from ?? ''}
                  periodTo={sourceInfo?.period_to ?? ''}
                  isSelected={selectedIds.includes(campaign.id)}
                  canSelect={selectedIds.length < MAX_COMPARE}
                  onSelect={handleSelect}
                  onRefClick={setRefPanelData}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Barra de comparacao */}
      {selectedIds.length >= 1 ? (
        <CompareBar
          selectedIds={selectedIds}
          campaigns={campaigns}
          onCompare={handleCompare}
          onClear={handleClearSelection}
        />
      ) : null}

      {/* Painel de rastreabilidade */}
      {refPanelData !== null ? (
        <AdsRefPanel data={refPanelData} onClose={() => setRefPanelData(null)} />
      ) : null}
    </div>
  );
}

// Exporta o componente AdsRefPanel e helpers para reuso nas outras telas
export { AdsRefPanel, fmtCurrency, fmtNumber, fmtPercent, statusLabel, statusCssClass };
export type { CampaignKpis, CampaignItem, RefPanelData };
